import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";
import { PART_GROUPS_QUERY_KEY } from "./PartGroupService";

// ===========================
// Types
// ===========================

export interface PartMatch {
  part_id: number;
  name: string;
  part_type?: string | null;
  manufacturer?: string | null;
  description?: string | null;
  distance: number | null;
}

export interface Part {
  id: number;
  name: string;
  material_number?: string | null;
  part_type?: string | null;
  manufacturer?: string | null;
  manufacturer_fk?: number | null;
  plant_fk?: number | null;
  description?: string | null;
  photo_artifact_fk?: number | null;
  features: Record<string, string>;
  feature_order: string[];
  has_embedding: boolean;
  parts_ai_run_fk?: number | null;
  part_group_fk?: number | null;
  part_schema_fk?: number | null;
  create_user_fk: number;
  create_date: string;
  matches: PartMatch[];
}

export interface PartListItem {
  id: number;
  name: string;
  material_number?: string | null;
  part_type?: string | null;
  manufacturer?: string | null;
  manufacturer_fk?: number | null;
  plant_fk?: number | null;
  description?: string | null;
  photo_artifact_fk?: number | null;
  has_embedding: boolean;
  part_group_fk?: number | null;
  part_schema_fk?: number | null;
  create_user_fk: number;
  create_date: string;
}

export interface PaginatedParts {
  items: PartListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface CreatePartPayload {
  name: string;
  material_number?: string | null;
  part_type?: string | null;
  manufacturer?: string | null;
  manufacturer_fk?: number | null;
  plant_fk?: number | null;
  description?: string | null;
  photo_artifact_fk?: number | null;
  features?: Record<string, string>;
  part_group_fk?: number | null;
}

export interface UpdatePartPayload {
  name?: string;
  material_number?: string | null;
  part_type?: string | null;
  manufacturer?: string | null;
  manufacturer_fk?: number | null;
  plant_fk?: number | null;
  description?: string | null;
  photo_artifact_fk?: number | null;
  features?: Record<string, string>;
  part_group_fk?: number | null;
}

export interface SaveAsPartPayload {
  name: string;
  part_type?: string | null;
  manufacturer?: string | null;
  description?: string | null;
  photo_artifact_fk?: number | null;
  part_group_fk?: number | null;
}

export interface PartExcelUploadResponse {
  created_count: number;
  error_count: number;
  errors: Array<{ row: number; error: string }>;
  part_ids: number[];
}

export interface ListPartsParams {
  part_group_fk?: number;
  part_type?: string;
  manufacturer?: string;
  search?: string;
  /**
   * Excluded and counted server-side once MRE #30789 merges. Older backends
   * ignore the unknown param; the picker then filters the returned page
   * client-side and the paginated total can read one high.
   */
  exclude_part_id?: number;
  page?: number;
  page_size?: number;
}

// ===========================
// Query Keys
// ===========================

export const PARTS_QUERY_KEY = "parts-ai.parts";
export const PART_DETAIL_QUERY_KEY = "parts-ai.partDetail";
export const PART_MATCHES_QUERY_KEY = "parts-ai.partMatches";
export const LINKED_PARTS_QUERY_KEY = "parts-ai.linkedParts";

// ===========================
// API Functions
// ===========================

// Wrapped in a function so `nuclearn_api_url` is read at call-time, not at
// module-load time — avoids a TDZ ReferenceError if a circular import chain
// reaches this file before App.tsx finishes initializing the export.
export const partsBase = () => `${nuclearn_api_url}/products/parts`;

const createPart = async (payload: CreatePartPayload): Promise<Part> => {
  const response = await axios.post<Part>(`${partsBase()}/parts`, payload);
  return response.data;
};

const listParts = async (
  params: ListPartsParams = {},
): Promise<PaginatedParts> => {
  const response = await axios.get<PaginatedParts>(`${partsBase()}/parts`, {
    params,
  });
  return response.data;
};

const getPart = async (partId: number): Promise<Part> => {
  const response = await axios.get<Part>(`${partsBase()}/parts/${partId}`);
  return response.data;
};

const updatePart = async (
  partId: number,
  payload: UpdatePartPayload,
): Promise<Part> => {
  const response = await axios.put<Part>(
    `${partsBase()}/parts/${partId}`,
    payload,
  );
  return response.data;
};

const deletePart = async (partId: number): Promise<void> => {
  await axios.delete(`${partsBase()}/parts/${partId}`);
};

export interface BatchDeleteResponse {
  deleted_count: number;
  error_count: number;
  errors: Array<{ part_id: number; error: string }>;
  part_ids: number[];
}

const bulkDeleteParts = async (
  partIds: number[],
): Promise<BatchDeleteResponse> => {
  const response = await axios.post<BatchDeleteResponse>(
    `${partsBase()}/parts/batch-delete`,
    { part_ids: partIds },
  );
  return response.data;
};

const triggerPartEmbedding = async (
  partId: number,
): Promise<{ status: string; part_id: number }> => {
  const response = await axios.post(`${partsBase()}/parts/${partId}/embed`);
  return response.data;
};

const getPartMatches = async (partId: number): Promise<PartMatch[]> => {
  const response = await axios.get<PartMatch[]>(
    `${partsBase()}/parts/${partId}/matches`,
  );
  return response.data;
};

const saveRunAsPart = async (
  runId: number,
  payload: SaveAsPartPayload,
): Promise<Part> => {
  const response = await axios.post<Part>(
    `${partsBase()}/runs/${runId}/save-as-part`,
    payload,
  );
  return response.data;
};

const uploadPartsExcel = async (
  file: File,
  partGroupFk?: number,
): Promise<PartExcelUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const params = partGroupFk ? { part_group_fk: partGroupFk } : {};
  const response = await axios.post<PartExcelUploadResponse>(
    `${partsBase()}/parts/upload`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      params,
    },
  );
  return response.data;
};

// ===========================
// React Query Hooks
// ===========================

export const useListParts = (params: ListPartsParams = {}) => {
  return useQuery([PARTS_QUERY_KEY, params], () => listParts(params), {
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  });
};

export const useGetPart = (partId: number | undefined) => {
  return useQuery([PART_DETAIL_QUERY_KEY, partId], () => getPart(partId!), {
    enabled: !!partId,
    refetchOnWindowFocus: false,
  });
};

export const useGetPartMatches = (partId: number | undefined) => {
  return useQuery(
    [PART_MATCHES_QUERY_KEY, partId],
    () => getPartMatches(partId!),
    {
      enabled: !!partId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useCreatePart = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: CreatePartPayload) => createPart(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries([PARTS_QUERY_KEY]);
      queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
    },
  });
};

export const useUpdatePart = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ partId, payload }: { partId: number; payload: UpdatePartPayload }) =>
      updatePart(partId, payload),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([PARTS_QUERY_KEY]);
        queryClient.invalidateQueries([
          PART_DETAIL_QUERY_KEY,
          variables.partId,
        ]);
        // Editing name/description/features re-embeds the part, which
        // recomputes matches asynchronously — refresh them too.
        queryClient.invalidateQueries([
          PART_MATCHES_QUERY_KEY,
          variables.partId,
        ]);
      },
    },
  );
};

export const useDeletePart = () => {
  const queryClient = useQueryClient();
  return useMutation((partId: number) => deletePart(partId), {
    onSuccess: () => {
      queryClient.invalidateQueries([PARTS_QUERY_KEY]);
    },
  });
};

export const useBulkDeleteParts = () => {
  const queryClient = useQueryClient();
  return useMutation((partIds: number[]) => bulkDeleteParts(partIds), {
    onSuccess: (_data, partIds) => {
      queryClient.invalidateQueries([PARTS_QUERY_KEY]);
      queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
      partIds.forEach((id) => {
        queryClient.invalidateQueries([PART_DETAIL_QUERY_KEY, id]);
        queryClient.invalidateQueries([PART_MATCHES_QUERY_KEY, id]);
      });
    },
  });
};

export const useTriggerPartEmbedding = () => {
  const queryClient = useQueryClient();
  return useMutation((partId: number) => triggerPartEmbedding(partId), {
    onSuccess: (_data, partId) => {
      queryClient.invalidateQueries([PART_DETAIL_QUERY_KEY, partId]);
    },
  });
};

export const useSaveRunAsPart = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ runId, payload }: { runId: number; payload: SaveAsPartPayload }) =>
      saveRunAsPart(runId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PARTS_QUERY_KEY]);
        queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
      },
    },
  );
};

export const useUploadPartsExcel = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ file, partGroupFk }: { file: File; partGroupFk?: number }) =>
      uploadPartsExcel(file, partGroupFk),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PARTS_QUERY_KEY]);
        queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
      },
    },
  );
};

// ===========================
// Structured feature search + facets (schema-driven filtering)
// ===========================

export type NumericOp = "gt" | "gte" | "lt" | "lte" | "eq";

export interface NumericFeaturePredicate {
  key: string;
  op: NumericOp;
  value: number;
}

export interface PartsSearchParams {
  part_type?: string;
  part_schema_fk?: number;
  part_group_fk?: number;
  /** Free-text term OR-ed across the standard columns. */
  search?: string;
  /** Canonical feature name -> accepted values (OR within a key, AND across keys). */
  string_feature_predicates?: Record<string, string[]>;
  numeric_feature_predicates?: NumericFeaturePredicate[];
  page?: number;
  page_size?: number;
}

export interface PartFacetValue {
  value: string;
  count: number;
}

export interface PartNumericRange {
  min: number;
  max: number;
}

export interface PartFacetsResponse {
  /** Canonical feature name -> distinct value counts (string/boolean features). */
  facets: Record<string, PartFacetValue[]>;
  /** Canonical feature name -> min/max (integer/float features). */
  numeric_ranges: Record<string, PartNumericRange>;
}

export interface PartFacetsParams {
  part_schema_fk: number;
  part_group_fk?: number;
  search?: string;
  string_feature_predicates?: Record<string, string[]>;
  numeric_feature_predicates?: NumericFeaturePredicate[];
}

export const PARTS_SEARCH_QUERY_KEY = "parts-ai.partsSearch";
export const PART_FACETS_QUERY_KEY = "parts-ai.partFacets";

const searchParts = async (
  body: PartsSearchParams,
): Promise<PaginatedParts> => {
  const response = await axios.post<PaginatedParts>(
    `${partsBase()}/parts/search`,
    body,
  );
  return response.data;
};

const getPartFacets = async (
  body: PartFacetsParams,
): Promise<PartFacetsResponse> => {
  const response = await axios.post<PartFacetsResponse>(
    `${partsBase()}/parts/facets`,
    body,
  );
  return response.data;
};

/**
 * Structured, paginated parts search by schema features (+ free text).
 * Pass `enabled: false` to hold off until a part type / filters are chosen.
 */
export const useSearchParts = (
  params: PartsSearchParams,
  options: { enabled?: boolean } = {},
) => {
  return useQuery([PARTS_SEARCH_QUERY_KEY, params], () => searchParts(params), {
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 10 * 1000,
  });
};

/**
 * Dynamic facet counts/ranges for a schema's features over the current
 * filtered set. Disabled until a `part_schema_fk` is supplied.
 */
export const useGetPartFacets = (params: PartFacetsParams | null) => {
  return useQuery(
    [PART_FACETS_QUERY_KEY, params],
    () => getPartFacets(params as PartFacetsParams),
    {
      enabled: !!params?.part_schema_fk,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 10 * 1000,
    },
  );
};

// ===========================
// Part Comparison
// ===========================

export interface PartComparison {
  id: number;
  dialogue_id: number;
  part_a_id: number;
  part_b_id: number;
  create_user_fk: number;
  create_date: string | null;
}

export interface StartPartComparisonPayload {
  partAId: number;
  partBId: number;
  chatConfigId?: number | null;
}

const startPartComparison = async (
  payload: StartPartComparisonPayload,
): Promise<PartComparison> => {
  const response = await axios.post<PartComparison>(
    `${partsBase()}/comparisons`,
    {
      part_a_id: payload.partAId,
      part_b_id: payload.partBId,
      chat_config_id: payload.chatConfigId ?? null,
    },
  );
  return response.data;
};

export const useStartPartComparison = () => {
  return useMutation((payload: StartPartComparisonPayload) =>
    startPartComparison(payload),
  );
};

// ===========================
// Chat with an existing part
// ===========================

export interface StartPartChatPayload {
  partId: number;
  /**
   * Chat config / persona to anchor the auto-created dialogue to. Required
   * for ``ranking_type`` to be stamped on assistant messages (otherwise the
   * thumbs-up / thumbs-down UI never appears).
   */
  chatConfigId?: number | null;
}

export interface StartPartChatResponse {
  run_id: number;
  dialogue_id: number;
}

const startPartChat = async (
  payload: StartPartChatPayload,
): Promise<StartPartChatResponse> => {
  const response = await axios.post<StartPartChatResponse>(
    `${partsBase()}/parts/${payload.partId}/chat`,
    {
      chat_config_id: payload.chatConfigId ?? null,
    },
  );
  return response.data;
};

export const useStartPartChat = () => {
  return useMutation((payload: StartPartChatPayload) => startPartChat(payload));
};

const saveRunToPart = async (runId: number): Promise<Part> => {
  const response = await axios.post<Part>(
    `${partsBase()}/runs/${runId}/save-to-part`,
  );
  return response.data;
};

export const useSaveRunToPart = () => {
  const queryClient = useQueryClient();
  return useMutation((runId: number) => saveRunToPart(runId), {
    onSuccess: (part) => {
      queryClient.invalidateQueries([PARTS_QUERY_KEY]);
      queryClient.invalidateQueries([PART_DETAIL_QUERY_KEY, part.id]);
      queryClient.invalidateQueries([PART_MATCHES_QUERY_KEY, part.id]);
    },
  });
};

// ===========================
// Linked parts (Parts tab)
// ===========================

export type LinkedPartRole =
  | "search"
  | "comparison_a"
  | "comparison_b"
  | "editing";

export interface LinkedPart {
  part: PartListItem;
  role: LinkedPartRole;
  /** Cosine distance for embedding-search hits; null otherwise. */
  distance: number | null;
  /** Insertion order within a search dialogue; null otherwise. */
  rank: number | null;
}

const listLinkedPartsForDialogue = async (
  dialogueId: number,
): Promise<LinkedPart[]> => {
  const response = await axios.get<LinkedPart[]>(
    `${partsBase()}/dialogues/${dialogueId}/linked-parts`,
  );
  return response.data;
};

/**
 * Read the set of parts linked to a Parts AI dialogue.
 */
export const useLinkedParts = (dialogueId: number | undefined) => {
  return useQuery(
    [LINKED_PARTS_QUERY_KEY, dialogueId],
    () => listLinkedPartsForDialogue(dialogueId!),
    {
      enabled: !!dialogueId,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );
};
